import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeNb from '@angular/common/locales/nb';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ConfigService } from '@app/config.service';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import { apiConfig, b2cPolicies, msalConfig } from '@env';
import { take, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

export function initializeApp(appInitService: ConfigService) {
  return (): Promise<any> => {
    return appInitService.Init();
  };
}

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {}

export function MSALInstanceFactory(): IPublicClientApplication {
  // if (window.location.href.indexOf('#id_token=') > -1)
  //   msalConfig.auth.authority = b2cPolicies.authorities.Custom_Login.authority;
  return new PublicClientApplication({
    auth: {
      clientId: msalConfig.auth.clientId,
      authority: msalConfig.auth.authority,
      redirectUri: msalConfig.auth.redirectUri,
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      knownAuthorities: [b2cPolicies.authorityDomain]
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(`${apiConfig.webApi + '/User'}`, apiConfig.scopes);
  protectedResourceMap.set(`${apiConfig.webApi + '/user'}`, apiConfig.scopes);
  protectedResourceMap.set(
    `${apiConfig.webApi + '/User/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/user/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/Application'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/Application/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/application/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(`${apiConfig.webApi + '/Role'}`, apiConfig.scopes);
  protectedResourceMap.set(`${apiConfig.webApi + '/role'}`, apiConfig.scopes);
  protectedResourceMap.set(
    `${apiConfig.webApi + '/Role/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/role/**'}`,
    apiConfig.scopes
  );
  protectedResourceMap.set(
    `${apiConfig.webApi + '/lookup/search-builder-params/by-screeen'}`,
    apiConfig.scopes
  );
  // All /Lookup/** endpoints are public — null scope tells MsalInterceptor to skip them.
  // Specific authenticated /lookup/search-builder-params is already listed above.
  protectedResourceMap.set(`${apiConfig.webApi + '/Lookup/**'}`, null);
  protectedResourceMap.set(`${apiConfig.webApi + '/Lookup'}`, null);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
    // ,
    // unprotectedResources: [
    //           ...AnonymousEndPoints,
    //           'http://localhost:4200/'
    //       ], // API calls to these coordinates will NOT activate MSALGuard
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes]
    }
  };
}

registerLocaleData(localeNb);

@NgModule({
  exports: [],
  declarations: [AppComponent],
  imports: [
    SharedModule,
    // hot-loaded features
    BrowserModule,
    // angular
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    // app
    AppRoutingModule,
    MsalModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    // {
    //   provide: DeviceDetectorService,
    //   useClass: UniversalDeviceDetectorService
    // },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    // {
    //   provide: DeviceDetectorService,
    //   useClass: DeviceDetectorService
    // },
    DatePipe,
    MsalService,    
    MsalGuard,
    MsalBroadcastService,
    ConfigService,
    provideAppInitializer(() => {
      const msalInstance = inject(MSAL_INSTANCE);
      const msalService = inject(MsalService);

      // handleRedirectObservable calls initialize() internally; process the B2C
      // redirect callback before the router strips the auth-code hash from the URL.
      return msalService.handleRedirectObservable().pipe(
        take(1),
        catchError(err => {
          console.warn('[MSAL] handleRedirect error:', err.message);
          return of(null);
        })
      ).toPromise().then(result => {
        const account = (result as any)?.account
          ?? msalInstance.getAllAccounts()[0]
          ?? null;
        if (account) {
          msalInstance.setActiveAccount(account);
        }
      });
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(ConfigService));
      return initializerFn();
    }),
    { provide: LOCALE_ID, useValue: 'en-us' }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
